import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BellOff, Loader2 } from 'lucide-react';
import { getDisplayErrorMessage } from '@/lib/api';
import { fetchChargerSnooze, setChargerSnooze } from '@/lib/alerts';
import styles from './ChargerDetailPage.module.css';

/**
 * Snooze this charger's alert emails (Warren, 29 Oct 2026, item 4: "Snooze 24 hours or 7 days, one click, in every
 * alert email and on the charger page, audited"; ruling F). For EVI ops' live emails, the only ones there are since 4 Nov
 * 2026; the digests still list it, marked. The buttons show only where the Api says a press would succeed (can_snooze: the
 * same check as acknowledging this charger's alerts), never by persona; anyone who cannot read the charger's alerts
 * sees nothing.
 */
export function AlertSnoozeCard({ chargerId }: { chargerId: string }) {
  const client = useQueryClient();
  const key = ['charger-alert-snooze', chargerId];
  const q = useQuery({ queryKey: key, queryFn: () => fetchChargerSnooze(chargerId), retry: false, staleTime: 30_000 });
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  if (q.isError) return null;
  const v = q.data;
  async function act(action: '24h' | '7d' | 'clear') {
    setBusy(true); setError('');
    try { client.setQueryData(key, await setChargerSnooze(chargerId, action)); }
    catch (err) { setError(await getDisplayErrorMessage(err, 'Not saved.')); } finally { setBusy(false); }
  }
  if (!v) return <div className={styles.dlmCap}><p className={styles.dlmCapLine}><Loader2 size={13} /> Loading.</p></div>;
  if (!v.active && !v.can_snooze) return null;
  return (
    <div className={styles.dlmCap}>
      <div className={styles.dlmCapHead}>
        <BellOff size={16} />
        <span className={styles.dlmCapTitle}>Alert emails</span>
        <span className={styles.dlmCapValue}>{v.active ? `Snoozed until ${new Date(v.active.until).toLocaleString()}` : 'On'}</span>
      </div>
      <p className={styles.dlmCapLine}>
        Snoozing stops the live emails about this charger to EVI ops; the morning summaries still list it, marked.
        {v.active ? ` Snoozed ${v.active.via === 'alert_email_link' ? 'from an alert email' : 'here'} for ${v.active.duration_label}.` : ''}
      </p>
      {v.can_snooze && <p className={styles.dlmCapLine}>
        <button type="button" disabled={busy} onClick={() => void act('24h')} style={{ marginRight: 6 }}>Snooze 24 hours</button>
        <button type="button" disabled={busy} onClick={() => void act('7d')} style={{ marginRight: 6 }}>Snooze 7 days</button>
        {v.active && <button type="button" disabled={busy} onClick={() => void act('clear')}>Unsnooze</button>}
      </p>}
      {error && <p className={styles.dlmCapLine} role="alert" style={{ color: 'var(--color-err)' }}>{error}</p>}
      {v.recent.length > 0 && <ul className={styles.dlmCapLine} style={{ margin: '8px 0 0', paddingLeft: 18 }}>
        {v.recent.map((r) => <li key={`${r.at}-${r.action}`}>
          {new Date(r.at).toLocaleString()}: {r.action === 'snoozed' ? `snoozed for ${r.duration === '7d' ? '7 days' : '24 hours'}${r.via === 'alert_email_link' ? ' from an alert email' : ''}` : 'unsnoozed'}
        </li>)}
      </ul>}
    </div>
  );
}
